import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@telegram-apps/telegram-ui";
import hapticFeedback from "../../utils/haptics";
import { useNavigate, useLocation } from "react-router-dom";
import { t } from "../../utils/i18n/index";
import {
  useGameStore,
  selectUser,
  selectEnergy,
  selectMaxEnergy,
  selectIsInitialized,
} from "../../store/gameStore";
import {
  useModalManager,
  ModalType,
  showOnboarding,
} from "../../utils/modal/ModalManager";
import { SpecialDealsService } from "../../services/SpecialDealsService";
import { InvitePromoService } from "../../services/InvitePromoService";
import { UserMax } from "../../handlers/types";
import Game from "../../scripts/game/Game";
import { MainMenuHeader } from "./MainMenuHeader";
import "./MainMenu.css";
import { StorageService } from "../../services/LocalStorageService";

interface MainMenuProps {
  className?: string;
}

export function MainMenu({
  className = "",
}: MainMenuProps): JSX.Element | null {
  const { openModal } = useModalManager();
  const navigate = useNavigate();
  const location = useLocation();

  // Use store selectors for reactive state
  const userData = useGameStore(selectUser);
  const energy = useGameStore(selectEnergy);
  const maxEnergy = useGameStore(selectMaxEnergy);
  const isInitialized = useGameStore(selectIsInitialized);

  const isFirstBalanceAccessRef = useRef(true);

  const updateUserData = useCallback(
    async (hardRefresh: boolean = true) => {
      if (isInitialized && hardRefresh) {
        try {
          // Refresh user data from API
          await useGameStore.getState().refreshUserData();
        } catch (error) {
          console.error(`Failed to refresh user data:`, error);
        }
      }
    },
    [isInitialized]
  );

  useEffect(() => {
    if (isInitialized) {
      // Check and show onboarding for first-time users
      const hasShownOnboarding =
        localStorage.getItem("onboarding_shown_v2") === "true";
      if (!hasShownOnboarding) {
        showOnboarding();
      } else {
        // For returning users: first try to show Invite Promo (if eligible), then Special Deals
        InvitePromoService.checkAndShowInvitePromo();
        SpecialDealsService.checkAndShowSpecialDeals();
      }
    }
  }, [isInitialized]);

  // Update user data when navigating to /menu route (skip on initial app load)
  useEffect(() => {
    if (location.pathname === "/menu" && isInitialized) {
      // Clear stage data when navigating to main menu via React Router
      if (Game.Core?.StageService) {
        Game.Core.StageService.clearStageData();
      }

      // Check if this is the first time accessing balance in this session
      if (isFirstBalanceAccessRef.current) {
        isFirstBalanceAccessRef.current = false;
        // Skip user data refresh on initial app load
        return;
      }
      // Only refresh on subsequent navigations to /menu
      updateUserData(true);
    }
  }, [location.pathname, isInitialized]); // Removed isFirstBalanceAccess dependency

  const handlePlayClick = useCallback(() => {
    if (energy > 0) {
      navigate("/stage-select");
    } else {
      openModal(ModalType.ENERGY_POPUP, {});
    }
  }, [energy, navigate, openModal]);

  const handleUpgradeClick = useCallback(() => {
    const characterId = Game.Core?.PlayerOptions?.SelectedCharacterId || "";
    const path = characterId
      ? `/character-upgrade/${characterId}`
      : "/character-upgrade";
    navigate(path);
  }, [navigate]);

  const handleRefillClick = useCallback(() => {
    openModal(ModalType.ENERGY_POPUP, {});
  }, [openModal]);

  const handleSettingsClick = useCallback(() => {
    navigate("/settings");
  }, [navigate]);

  const handleMagicBooksClick = useCallback(() => {
    // Dismiss one-time hint for Books button when clicked first time
    if (
      !StorageService.isOneTimeHintDismissed(
        StorageService.ONE_TIME_HINT_KEYS.MAIN_BOOKS_BUTTON
      )
    ) {
      StorageService.dismissOneTimeHint(
        StorageService.ONE_TIME_HINT_KEYS.MAIN_BOOKS_BUTTON
      );
    }
    navigate("/magic-books");
  }, [navigate]);

  return (
    <div className={`main-menu ${className}`}>
      {/* Background */}
      <div className="main-menu__background">
        <img
          src="/assets/img/ui/main_screen_bg.avif"
          alt="Background"
          className="main-menu__background-image"
        />
      </div>

      {/* Header */}
      <MainMenuHeader
        userData={userData}
        onSettingsClick={handleSettingsClick}
        onUserDataRefresh={() => updateUserData(true)}
      />

      {/* Main Content */}
      <div className="main-menu__content">
        {/* Action Buttons */}
        <div className="main-menu__actions">
          <Button
            size="l"
            stretched
            className="main-menu__play-button"
            onClick={handlePlayClick}
            after={
              energy > 0 ? (
                <img src="/assets/img/ui/energy_full.webp" alt="Energy" />
              ) : (
                <img src="/assets/img/ui/energy_empty.webp" alt="Energy" />
              )
            }
          >
            {t("common.play")}
          </Button>

          <div className="main-menu__magic-books-button-wrapper">
            <Button
              size="l"
              stretched
              className="main-menu__magic-books-button"
              onClick={handleMagicBooksClick}
            >
              {t("common.magic_books")}
            </Button>
            {!StorageService.isOneTimeHintDismissed(
              StorageService.ONE_TIME_HINT_KEYS.MAIN_BOOKS_BUTTON
            ) && <div className="notification-dot notification-dot--corner" />}
          </div>

          <Button
            size="l"
            stretched
            className="main-menu__upgrade-button"
            onClick={handleUpgradeClick}
          >
            {t("common.upgrade_btn")}
          </Button>
        </div>
      </div>

      {/* Energy Panel */}
      <div className="main-menu__energy-panel">
        <div className="main-menu__energy-background">
          <div
            className="main-menu__energy-progress"
            style={{
              width: `${maxEnergy > 0 ? (energy / maxEnergy) * 100 : 0}%`,
            }}
          />
        </div>
        <div className="main-menu__energy-content">
          <div className="main-menu__energy-info">
            <img
              src="/assets/img/ui/energy_full.webp"
              alt="Energy"
              className="main-menu__energy-icon"
            />
            <span className="main-menu__energy-count">{energy}</span>
          </div>
          {energy <= 0 && (
            <Button
              className="main-menu__refill-button"
              onClick={handleRefillClick}
              size="s"
            >
              {t("common.refill")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
